<template>
    <Skeleton v-if="initialDataLoader.pending.value" :items="['block', `${APEX_SKELETON}`]"/>
    <template v-else>
        <Blocks>
            <Block v-if="isSet(monthRanger.selectedRenderSet.value.auszahlung)" :color="payrollChartColors.auszahlung">
                <template #bigNumber>{{money.formatLargeMoney(monthRanger.selectedRenderSet.value.auszahlung)}}</template>
                <template #subTitle v-if="monthRanger.selectedRenderSet.value.netto && !money.eq(monthRanger.selectedRenderSet.value.netto, monthRanger.selectedRenderSet.value.auszahlung)">
                    Lohnauszahlung
                    <br/>
                    Netto: {{money.formatLargeMoney(monthRanger.selectedRenderSet.value.netto)}}
                </template>
                <template #subTitle v-else>
                    Nettolohn<br>&nbsp;
                </template>
            </Block>

            <Block v-if="hasMobilityCosts && isSet(monthRanger.selectedRenderSet.value.mobilityCost)" :color="payrollChartColors.mobilityCost">
                <template #bigNumber>{{money.formatLargeMoney(monthRanger.selectedRenderSet.value.mobilityCost)}}</template>
                <template #subTitle>Kosten für Mobilität</template>
            </Block>
            <Block v-else-if="!hasMobilityCosts && isSet(monthRanger.selectedRenderSet.value.bruttoAN)" :color="payrollChartColors.mobilityCost">
                <template #bigNumber>{{money.formatLargeMoney(monthRanger.selectedRenderSet.value.bruttoAN)}}</template>
                <template #subTitle>Bruttolohn<br>&nbsp;</template>
            </Block>

            <Block v-if="isSet(monthRanger.selectedRenderSet.value.pk1)">
                <template #bigNumber>{{money.formatLargeMoney(monthRanger.selectedRenderSet.value.pk1)}}</template>
                <template #subTitle>Personalaufwand gesamt</template>
            </Block>
        </Blocks>

        <IonButton
            class="chart-toggle"
            color="light"
            expand="block"
            @click="showPayrollChart = !showPayrollChart"
        >
            Details {{showPayrollChart ? 'ausblenden' : 'anzeigen'}}
        </IonButton>

        <div  v-if="showPayrollChart" >
            <div class="boxed margin-bottom-regular">
                <ApexChartValueLine :values="[
                    {
                        label: 'Auszahlung',
                        value: monthRanger.selectedRenderSet.value.auszahlung,
                        formatter: (value: number|Money) => money.formatLargeMoney(value as Money),
                        color: payrollChartColors.auszahlung
                    },
                    {
                        label: 'Brutto',
                        value: monthRanger.selectedRenderSet.value.bruttoWithoutMobility,
                        formatter: (value: number|Money) => money.formatLargeMoney(value as Money),
                        color: payrollChartColors.bruttoAN
                    },
                    {
                        label: 'LNK',
                        value: monthRanger.selectedRenderSet.value.lohnnebenkostenAG,
                        formatter: (value: number|Money) => money.formatLargeMoney(value as Money, {signDisplay: 'exceptZero'}),
                        color: payrollChartColors.lohnnebenkostenAG
                    }
                ].concat(
                    hasMobilityCosts
                        ? [{
                                label: 'Mobi',
                                value: monthRanger.selectedRenderSet.value.mobilityCost,
                                formatter: (value: number|Money) => money.formatLargeMoney(value as Money, {signDisplay: 'exceptZero'}),
                                color: payrollChartColors.mobilityCost
                            }]
                        : []
                )"/>
                <ApexChartWrapper :options="apexOptions"/>
            </div>

            <IonLabel class="hint globalJustifyHyphenate">
                Zusätzlich zum Bruttolohn entstehen dem Arbeitgeber Lohnnebenkosten (<strong>LNK</strong>). Hierin sind sowohl die Arbeitgeberanteile der Renten-, Kranken-, Pflege- und Arbeitslosenversicherungen enthalten sowie die Unfallversicherung und Umlagebeiträge.
                <template v-if="hasMobilityCosts">
                    Hinzu kommen die
                    <DetailsPopover label="Kosten für Mobilität" text-only inline side="bottom">
                        <p class="globalJustifyHyphenate">Bei einem Dienstwagen setzen sich diese aus den Mietgebühren sowie einer Pauschale für die Tankkosten zusamen.</p>
                            <p class="globalJustifyHyphenate">In der Lohnabrechnung wird ein Teil der Mobilitätskosten als Sachbezug auf den Bruttolohn aufgeschlagen (geldwerter Vorteil).</p>
                    </DetailsPopover>.
                </template>

            </IonLabel>

            <a target="_blank" href="https://409073.landwehr-hosting.de/index.php?page=Login&login=Personal">
                <RichResponseButton title="Lohnabrechnung herunterladen"/>
            </a>
        </div>
    </template>
</template>

<script setup lang="ts">
import {computed, PropType, ref, watch} from 'vue';
import {CalendarMonth} from "@/helper/calendar/calendarMonth";
import {DataLoader} from "@/helper/dataLoader";

import Skeleton from "@/components/Skeleton.vue";
import {Money, PayrollStatus} from "@/graphql/generated/graphql";

import {kapitelMonthString} from '@/graphql/kapitelTypes';
import {APEX_SKELETON, generateApexOptions} from "@/helper/apexchart";
import {fetchPayrollStatusList} from "@/helper/monthKPI/monthKpiPayroll";
import {MonthKPIApexMonthRangerFactory} from "@/helper/monthKPI/monthKPI";
import {toMap} from "@/helper/array";
import {ifIsSet, isSet, nullish} from "@/helper/isset";
import * as money from "@/helper/money";
import ApexChartValueLine from "@/views/components/ApexChartValueLine.vue";
import ApexChartWrapper from "@/views/components/ApexChartWrapper.vue";
import Blocks from "@/views/components/Blocks.vue";
import Block from "@/views/components/Block.vue";
import {IonButton, IonLabel} from "@ionic/vue";
import RichResponseButton from "@/views/Chat/RichResponses/components/RichResponseButton.vue";
import Ines from "@/views/components/Ines.vue";
import DetailsPopover from "@/views/components/DetailsPopover.vue";

// props
const selectedMonth = defineModel({
    type: String as PropType<kapitelMonthString>,
    required: true,
})

// loader
const initialDataLoader = new DataLoader();
const showPayrollChart = ref(false);


/**
 * raw data
 */
const payrollStatusList = ref<PayrollStatus[]>([])
const payrollStatusMap = computed(() => toMap(payrollStatusList.value, (a) => a.month as kapitelMonthString))
initialDataLoader.add(
    async () => payrollStatusList.value = await fetchPayrollStatusList(monthRanger.allMonths)
)

const hasMobilityCosts = computed(() => payrollStatusList.value.some(status => money.gt(status.mobilityCost, money.Zero)))


/**
 * month range & selection
 */
const monthRanger = MonthKPIApexMonthRangerFactory({
    selected: selectedMonth.value,
    renderer: (calendarMonth: CalendarMonth) => {
        const payrollStatus = payrollStatusMap.value.get(calendarMonth.getId())
        const lohnnebenkostenAG = payrollStatus?.lohnnebenkostenAG ?? null

        return {
            pk1: payrollStatus?.pk1 ?? null,

            lohnnebenkostenAG,

            bruttoAN: payrollStatus?.bruttoAN ?? null,
            bruttoWithoutMobility: payrollStatus?.bruttoWithoutMobility ?? null,
            netto: payrollStatus?.netto ?? null,

            mobilityCost: payrollStatus?.mobilityCost ?? null,
            auszahlung: payrollStatus?.auszahlung ?? null,
        }
    }
})

watch(() => monthRanger.selected.value, (month: kapitelMonthString) => selectedMonth.value = month)

const payrollSeries = computed(() => ([
    {
        name: "Auszahlung",
        data: monthRanger.allMonthsRenderSets.map(rs => ifIsSet(rs.value.auszahlung, money.toFloat, null)),
    },
    {
        name: "Brutto AN",
        data: monthRanger.allMonthsRenderSets.map(rs => ifIsSet(
            rs.value.bruttoAN && rs.value.auszahlung ? money.sub(rs.value.bruttoAN,rs.value.auszahlung) : null,
            money.toFloat, null)
        ),
        type: 'bar',
    },
    {
        name: "lohnNebenkostenAG",
        data: monthRanger.allMonthsRenderSets.map(rs => ifIsSet(rs.value.lohnnebenkostenAG, money.toFloat, null)),
        type: 'bar',
    },
    {
        name: "Mobilität",
        data: monthRanger.allMonthsRenderSets.map(rs => ifIsSet(rs.value.mobilityCost, money.toFloat, null)),
        type: 'bar',
    },
]))


const payrollChartColors = {
    auszahlung: 'var(--ion-color-green-tint)',
    bruttoAN: 'var(--ion-color-grey-tint-2-shade)',
    lohnnebenkostenAG: 'var(--ion-color-grey-tint-1-shade)',
    mobilityCost: 'var(--ion-color-grey-shade)'
}
const colors = [
    payrollChartColors.auszahlung,
    payrollChartColors.bruttoAN,
    payrollChartColors.lohnnebenkostenAG,
    payrollChartColors.mobilityCost,
]

const apexOptions = computed(() => {
    return generateApexOptions(
        monthRanger,
        {
            series: payrollSeries.value,
            chart: {
                type: 'bar',
                stacked: true
            },
            colors,
            legend: {
                show: false,
            },
            yaxis: {
                labels: {
                    formatter: (value: number|nullish) => isSet(value) ? money.formatLargeFloat(value, {notation:"compact", compactDisplay: "short"}, 'en-US') : '-',

                    // ensure equal width across all monthKPI charts
                    maxWidth: 50,
                    minWidth: 50
                }
            }
        },
        (idx: number) => {
            const rs = monthRanger.getRenderSet(idx)?.value
            return rs && isSet(rs.pk1) ? money.formatLargeMoney(rs.pk1 as Money) : null
        }
    )
})

/**
 * reset
 */

const reset = async () => initialDataLoader.reload()
defineExpose({reset})
reset()

</script>
